import React, { useMemo } from "react";
import {
  ExcelExportColumn,
  ExcelExport,
} from "@progress/kendo-react-excel-export";
import { useTranslation } from "react-i18next";
import { formatDateWithTime } from "utils/pdf";
import { useAppStoreState } from "store/AppStore";
import { useBankStatementsStoreState } from "store/BankStatementsStore";
import { bankStatementEntriesColumnConfig } from "../../BankStatementEntriesGrid/bankStatementEntriesColumnConfig";
import { withQtgInvoiceColumns } from "pages/ImportedInvoices/importedInvoiceUtils";
import { TFunction } from "i18next";
import moment from "moment";

type Props = {};

export const PerfectMatchExportExcel = React.forwardRef<ExcelExport, Props>(
  (props, ref) => {
    const { t } = useTranslation();
    const { taxonomy, grecoCompanies } = useAppStoreState();
    const cols = withQtgInvoiceColumns(bankStatementEntriesColumnConfig, grecoCompanies);
    const actualColumns = cols.filter(
      (c) =>
        c.columnId !== "ordinalNumber" &&
        c.columnId !== "select" &&
        c.columnId !== "bankstatemententryoptions" &&
        c.columnId !== "options" &&
        c.columnId !== "status" &&
        c.columnId !== "lastModified"
    );
    const { perfectMatchResults } = useBankStatementsStoreState();

    const data = useMemo(() => {
      return transformRowsForExcel({
        rows: perfectMatchResults.map((entry: any) => {
          return {
            ...entry,
            clientName: entry.clientName ?? "",
          };
        }),
        columns: actualColumns,
        t,
        taxonomy,
      });
    }, [actualColumns, perfectMatchResults, taxonomy]);

    return (
      <ExcelExport
        data={data}
        ref={ref}
        fileName={`Perfect Match Results ${formatDateWithTime(new Date())}.xlsx`}
      >
        {actualColumns.map((col) => {
          let format = null;
          let align: "center" | "left" | "right" = "center";
          if (col.cellType === "customtext") {
            format = "text";
            align = "left";
          } else if (col.cellType === "customnumber") {
            format = "#,##0.00";
            align = "right";
          } else if (col.cellType === "datepicker") {
            format = "dd/MM/yyyy";
            align = "center";
          } else if (col.cellType === "taxonomy") {
            format = "text";
            align = "left";
          } else if (col.cellType === "client") {
            format = "text";
            align = "left";
          }
          return (
            <ExcelExportColumn
              key={col.columnId}
              field={col.columnId}
              title={t(col.label)}
              cellOptions={{ format: format }}
              width={col.width}
              headerCellOptions={{
                background: "#005aa1",
                color: "#fff",
                textAlign: align,
              }}
            />
          );
        })}
      </ExcelExport>
    );
  }
);

export const transformRowsForExcel = ({
  rows,
  columns,
  taxonomy,
  t,
}: {
  rows: any[];
  columns: any[];
  taxonomy: any;
  t: TFunction;
}) => {
  if (!rows) return [];

  return rows.map((v) => {
    return columns.reduce((transformRow, c) => {
      if (c.cellType === "customtext") {
        transformRow[c.columnId] = v[c.columnId];
      } else if (c.cellType === "customnumber") {
        transformRow[c.columnId] = v[c.columnId];
      } else if (c.cellType === "datepicker") {
        transformRow[c.columnId] = v[c.columnId]
          ? moment(v[c.columnId]).format("DD.MM.YYYY")
          : "";
      } else if (c.cellType === "taxonomy") {
        transformRow[c.columnId] = t(
          taxonomy[c.additionalInfo?.taxonomy]?.byId[v[c.columnId]]?.code
        );
      } else if (c.cellType === "client") {
        const clientInfo = v["clientInfo"];
        transformRow[c.columnId] = clientInfo
          ? clientInfo.name + " (" + clientInfo.internalNumber + ")"
          : "";
      }
      return transformRow;
    }, {});
  });
};
